/**
 * User ID Utilities
 * 
 * Centralized utilities for handling user ID conversions throughout the app.
 * 
 * ## Context
 * User IDs can be either strings or numbers depending on the source:
 * - Auth context may return string IDs
 * - API responses may return numeric IDs
 * - Database expects numeric IDs
 * 
 * This utility provides type-safe conversion with proper error handling.
 * 
 * @module lib/utils/userIdUtils
 */

/**
 * User ID type - can be string or number
 */
export type UserId = string | number;

/**
 * Convert user ID to number
 * 
 * Handles both string and number inputs safely.
 * 
 * @param userId - User ID (string or number)
 * @returns Numeric user ID
 * @throws Error if userId is null/undefined or cannot be converted
 * 
 * @example
 * ```typescript
 * const userId = toNumericUserId(user.id); // Works with string or number
 * ```
 */
export function toNumericUserId(userId: UserId | null | undefined): number {
  if (userId === null || userId === undefined) {
    throw new Error('User ID is required');
  }

  if (typeof userId === 'number') {
    return userId;
  }

  const numericId = parseInt(userId, 10);
  
  if (isNaN(numericId)) {
    throw new Error(`Invalid user ID: ${userId}`);
  }

  return numericId;
}

/**
 * Safely convert user ID to number, returning null on failure
 * 
 * Use this variant when you want to handle missing/invalid IDs gracefully.
 * 
 * @param userId - User ID (string or number)
 * @returns Numeric user ID or null if invalid
 * 
 * @example
 * ```typescript
 * const userId = safeToNumericUserId(user?.id);
 * if (userId === null) {
 *   // Handle missing ID
 * }
 * ```
 */
export function safeToNumericUserId(userId: UserId | null | undefined): number | null {
  try {
    return toNumericUserId(userId);
  } catch {
    return null;
  }
}
